import React, { useState, useEffect } from 'react';
import { User, Truck, Phone, MapPin, AlertCircle, Loader2, Star, Calendar, Award, Eye, EyeOff } from 'lucide-react';

const SoferiPage = ({ 
  loading: parentLoading = false, 
  error: parentError = null,
  onFetchDrivers,
  onFetchVehicles 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemType, setItemType] = useState(null); // 'driver' or 'vehicle'
  const [filterStatus, setFilterStatus] = useState('all');
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  
  // State pentru datele din API
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Încarcă datele la mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Încarcă șoferii și vehiculele în paralel
      const [driversResult, vehiclesResult] = await Promise.all([
        onFetchDrivers ? onFetchDrivers() : { success: false, data: [] },
        onFetchVehicles ? onFetchVehicles() : { success: false, data: [] }
      ]);

      if (driversResult.success) {
        // Transformă datele șoferilor în formatul așteptat
        const transformedDrivers = driversResult.data.map(driver => ({
          id: driver.id,
          nume: driver.name || driver.nume || 'Nume necunoscut',
          telefon: driver.phone || driver.telefon || 'N/A',
          email: driver.email || 'N/A',
          permis: driver.driving_license || driver.permis || 'N/A',
          dataAngajarii: driver.hire_date || driver.dataAngajarii || driver.created_at || 'N/A',
          adresa: driver.address || driver.adresa || 'N/A',
          vehiculAsignat: driver.assigned_vehicle || driver.vehiculAsignat || null,
          status: driver.status || 'activ',
          rating: driver.rating || 4.5,
          totalExpedieri: driver.total_shipments || driver.totalExpedieri || 0,
          username: driver.username || '',
          role: driver.role || 'sofer'
        }));
        setDrivers(transformedDrivers);
      } else {
        console.warn('Nu s-au putut încărca șoferii:', driversResult.error);
        setDrivers([]);
      }

      if (vehiclesResult.success) {
        // Transformă datele vehiculelor în formatul așteptat
        const transformedVehicles = vehiclesResult.data.map(vehicle => ({
          id: vehicle.id,
          numar: vehicle.plate_number || vehicle.numar || 'N/A',
          marca: vehicle.brand || vehicle.marca || 'N/A',
          model: vehicle.model || 'N/A',
          an: vehicle.year || vehicle.an || 'N/A',
          status: determineVehicleStatus(vehicle),
          soferAsignat: vehicle.assigned_driver || vehicle.soferAsignat || null,
          ultimaRevizie: vehicle.last_service || vehicle.ultimaRevizie || 'N/A',
          kmBord: vehicle.km || vehicle.kmBord || '0',
          tip: vehicle.type || vehicle.tip || 'Camion',
          combustibil: vehicle.fuel_type || vehicle.combustibil || 'Diesel',
          capacitate: vehicle.capacity || vehicle.capacitate || 'N/A'
        }));
        setVehicles(transformedVehicles);
      } else {
        console.warn('Nu s-au putut încărca vehiculele:', vehiclesResult.error);
        setVehicles([]);
      }

    } catch (err) {
      console.error('Eroare la încărcarea datelor:', err);
      setError('Eroare la încărcarea datelor');
      
      
    } finally {
      setLoading(false);
    }
  };

  // Determină statusul vehiculului pe baza datelor
  const determineVehicleStatus = (vehicle) => {
    if (vehicle.status) return vehicle.status;
    if (vehicle.assigned_driver || vehicle.soferAsignat) return 'ocupat';
    return 'liber';
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('ro-RO');
    } catch {
      return dateString;
    }
  };

  const formatKm = (km) => {
    if (!km || km === 'N/A') return 'N/A';
    const numKm = parseInt(km.toString().replace(/[^\d]/g, ''));
    return isNaN(numKm) ? km : `${numKm.toLocaleString('ro-RO')} km`;
  };

  // Crează perechi driver-vehicle sau doar vehicule neasignate
  const getDisplayItems = () => {
    if (showUnassignedOnly) {
      return vehicles
        .filter(vehicle => !vehicle.soferAsignat)
        .filter(vehicle => 
          vehicle.numar.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.model.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }

    const pairs = [];
    const assignedVehicleNumbers = new Set();

    // Adaugă perechi driver-vehicle
    drivers.forEach(driver => {
      const assignedVehicle = vehicles.find(v => v.numar === driver.vehiculAsignat);
      if (assignedVehicle) {
        assignedVehicleNumbers.add(assignedVehicle.numar);
      }
      
      const matchesSearch = driver.nume.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.telefon.includes(searchTerm) ||
        (driver.vehiculAsignat && driver.vehiculAsignat.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesFilter = filterStatus === 'all' || driver.status === filterStatus;
      
      if (matchesSearch && matchesFilter) {
        pairs.push({
          type: 'pair',
          driver,
          vehicle: assignedVehicle
        });
      }
    });

    return pairs;
  };

  const displayItems = getDisplayItems();

  const getStatusColor = (status) => {
    switch (status) {
      case 'activ': 
      case 'active': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'pe_drum': 
      case 'on_route': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'incarcare': 
      case 'loading': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'pauza': 
      case 'break': return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'indisponibil':
      case 'unavailable': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getVehicleStatusColor = (status) => {
    switch (status) {
      case 'liber': 
      case 'available': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'ocupat': 
      case 'occupied': return 'bg-red-100 text-red-800 border-red-200';
      case 'pe_drum': 
      case 'on_route': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'incarcare': 
      case 'loading': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'disponibil': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'service': 
      case 'mentenanta': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'activ': 'Activ',
      'active': 'Activ',
      'pe_drum': 'Pe drum',
      'on_route': 'Pe drum',
      'incarcare': 'În încărcare',
      'loading': 'În încărcare',
      'pauza': 'Pauză',
      'break': 'Pauză',
      'indisponibil': 'Indisponibil',
      'unavailable': 'Indisponibil'
    };
    return statusLabels[status] || status;
  };

  const getVehicleStatusLabel = (status) => {
    const statusLabels = {
      'liber': 'Liber',
      'available': 'Liber',
      'ocupat': 'Ocupat',
      'occupied': 'Ocupat',
      'pe_drum': 'Pe drum',
      'on_route': 'Pe drum',
      'incarcare': 'În încărcare',
      'loading': 'În încărcare',
      'disponibil': 'Disponibil',
      'service': 'În service',
      'mentenanta': 'Mentenanță'
    };
    return statusLabels[status] || status;
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Star key="half" className="h-3 w-3 fill-yellow-400/50 text-yellow-400" />);
    }
    
    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-3 w-3 text-gray-300" />);
    }
    
    return stars;
  };

  const activeDrivers = drivers.filter(d => d.status === 'activ' || d.status === 'active').length;
  const driversOnRoute = drivers.filter(d => d.status === 'pe_drum' || d.status === 'on_route').length;
  const availableVehiclesCount = vehicles.filter(v => v.status === 'liber' || v.status === 'available').length;
  const unassignedVehiclesCount = vehicles.filter(v => !v.soferAsignat).length;

  if (loading || parentLoading) {
    return (
      <div className="flex-1 p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <span className="text-lg text-gray-600">Se încarcă datele...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Managementul Șoferilor</h1>
        <p className="text-gray-600">Vizualizare șoferi și vehicule asignate</p>
      </div>

      {/* Error Display */}
      {(error || parentError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl shadow-sm">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-3 text-red-500" />
            <div>
              <strong className="font-semibold">Eroare:</strong> {error || parentError}
            </div>
            <button 
              onClick={loadData}
              className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Reîncearcă
            </button>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1">
            <input
              type="text"
              placeholder="🔍 Căutare..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          
          {!showUnassignedOnly && (
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            >
              <option value="all">Toți șoferii</option>
              <option value="activ">Activi</option>
              <option value="pe_drum">Pe drum</option>
              <option value="pauza">În pauză</option>
              <option value="indisponibil">Indisponibili</option>
            </select>
          )}
          
          <button
            onClick={() => setShowUnassignedOnly(!showUnassignedOnly)}
            className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${
              showUnassignedOnly 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {showUnassignedOnly ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showUnassignedOnly ? 'Vedere normală' : 'Doar neasignate'}
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs">Șoferi Activi</p>
              <p className="text-2xl font-bold">{activeDrivers}</p>
            </div>
            <User className="h-8 w-8 text-blue-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg shadow p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-xs">Vehicule Libere</p>
              <p className="text-2xl font-bold">{availableVehiclesCount}</p>
            </div>
            <Truck className="h-8 w-8 text-emerald-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg shadow p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-xs">Pe Traseu</p>
              <p className="text-2xl font-bold">{driversOnRoute}</p>
            </div>
            <MapPin className="h-8 w-8 text-amber-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs">Neasignate</p>
              <p className="text-2xl font-bold">{unassignedVehiclesCount}</p>
            </div>
            <Award className="h-8 w-8 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900">
            {showUnassignedOnly ? `Vehicule Neasignate (${displayItems.length})` : `Șoferi și Vehicule (${displayItems.length})`}
          </h2>
          <button
            onClick={loadData}
            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            🔄 Actualizează
          </button>
        </div>
        
        <div className="max-h-[600px] overflow-y-auto">
          {displayItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {showUnassignedOnly ? <Truck className="h-8 w-8 text-gray-400" /> : <User className="h-8 w-8 text-gray-400" />}
              </div>
              <p className="text-lg font-medium">
                {showUnassignedOnly ? 'Nu există vehicule neasignate' : 'Nu s-au găsit rezultate pentru căutarea curentă'}
              </p>
            </div>
          ) : showUnassignedOnly ? (
            // Show only unassigned vehicles
            <div className="divide-y divide-gray-100">
              {displayItems.map((vehicle) => (
                <div 
                  key={vehicle.id} 
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedItem(vehicle);
                    setItemType('vehicle');
                  }}
                >
                  <div className="flex items-center">
                    <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow">
                      <Truck className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{vehicle.numar}</h3>
                          <p className="text-xs text-gray-600">{vehicle.marca} {vehicle.model} ({vehicle.an})</p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getVehicleStatusColor(vehicle.status)}`}>
                            {getVehicleStatusLabel(vehicle.status)}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">{vehicle.capacitate}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Show driver-vehicle pairs
            <div className="divide-y divide-gray-100">
              {displayItems.map((item, index) => (
                <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Driver Side */}
                    <div 
                      className="flex items-center cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors"
                      onClick={() => {
                        setSelectedItem(item.driver);
                        setItemType('driver');
                      }}
                    >
                      <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">{item.driver.nume}</h3>
                        <div className="flex items-center text-xs text-gray-600">
                          <Phone className="h-3 w-3 mr-1" />
                          {item.driver.telefon}
                        </div>
                        <div className="flex items-center mt-1">
                          {renderStars(item.driver.rating)}
                          <span className="ml-1 text-xs text-gray-500">({item.driver.rating})</span>
                        </div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(item.driver.status)}`}>
                        {getStatusLabel(item.driver.status)}
                      </span>
                    </div>

                    {/* Vehicle Side */}
                    <div 
                      className={`flex items-center p-2 rounded-lg transition-colors ${
                        item.vehicle 
                          ? 'cursor-pointer hover:bg-emerald-50' 
                          : 'bg-gray-50 border-2 border-dashed border-gray-300'
                      }`}
                      onClick={() => {
                        if (item.vehicle) {
                          setSelectedItem(item.vehicle);
                          setItemType('vehicle');
                        }
                      }}
                    >
                      {item.vehicle ? (
                        <>
                          <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow">
                            <Truck className="h-5 w-5 text-white" />
                          </div>
                          <div className="ml-3 flex-1">
                            <h3 className="text-sm font-semibold text-gray-900">{item.vehicle.numar}</h3>
                            <p className="text-xs text-gray-600">{item.vehicle.marca} {item.vehicle.model}</p>
                            <p className="text-xs text-gray-500">{formatKm(item.vehicle.kmBord)}</p>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getVehicleStatusColor(item.vehicle.status)}`}>
                            {getVehicleStatusLabel(item.vehicle.status)}
                          </span>
                        </>
                      ) : (
                        <div className="flex items-center justify-center w-full text-gray-400">
                          <Truck className="h-6 w-6 mr-2" />
                          <span className="text-sm">Fără vehicul asignat</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center mr-4 ${
                    itemType === 'driver' 
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                      : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                  }`}>
                    {itemType === 'driver' ? 
                      <User className="h-6 w-6 text-white" /> : 
                      <Truck className="h-6 w-6 text-white" />
                    }
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {itemType === 'driver' ? selectedItem.nume : selectedItem.numar}
                    </h3>
                    <p className="text-gray-600">
                      {itemType === 'driver' ? 'Detalii șofer' : 'Detalii vehicul'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setSelectedItem(null);
                    setItemType(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {itemType === 'driver' ? (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3">📞 Contact</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Telefon:</strong> {selectedItem.telefon}</p>
                      <p><strong>Email:</strong> {selectedItem.email}</p>
                      <p><strong>Adresa:</strong> {selectedItem.adresa}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-3">🚗 Profesional</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Permis:</strong> {selectedItem.permis}</p>
                      <p><strong>Data angajării:</strong> {formatDate(selectedItem.dataAngajarii)}</p>
                      <div className="flex items-center">
                        <strong className="mr-2">Status:</strong>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(selectedItem.status)}`}>
                          {getStatusLabel(selectedItem.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-semibold text-amber-800 mb-3">📊 Performanță</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <strong className="mr-2">Rating:</strong>
                        <div className="flex items-center">
                          {renderStars(selectedItem.rating)}
                          <span className="ml-2 text-gray-700">({selectedItem.rating}/5)</span>
                        </div>
                      </div>
                      <p><strong>Total expedieri:</strong> {selectedItem.totalExpedieri}</p>
                      <p><strong>Vehicul asignat:</strong> {selectedItem.vehiculAsignat || 'Neasignat'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-3">🚛 Identificare</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Număr:</strong> {selectedItem.numar}</p>
                      <p><strong>Marcă:</strong> {selectedItem.marca}</p>
                      <p><strong>Model:</strong> {selectedItem.model}</p>
                      <p><strong>An fabricație:</strong> {selectedItem.an}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3">⚙️ Specificații</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Tip:</strong> {selectedItem.tip}</p>
                      <p><strong>Capacitate:</strong> {selectedItem.capacitate}</p>
                      <p><strong>Combustibil:</strong> {selectedItem.combustibil}</p>
                      <p><strong>Kilometraj:</strong> {formatKm(selectedItem.kmBord)}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-semibold text-amber-800 mb-3">📋 Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <strong className="mr-2">Status:</strong>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getVehicleStatusColor(selectedItem.status)}`}>
                          {getVehicleStatusLabel(selectedItem.status)}
                        </span>
                      </div>
                      <p><strong>Șofer asignat:</strong> {selectedItem.soferAsignat || 'Neasignat'}</p>
                      <p><strong>Ultima revizie:</strong> {formatDate(selectedItem.ultimaRevizie)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setSelectedItem(null);
                    setItemType(null);
                  }}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors font-medium"
                >
                  Închide
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoferiPage;